/**
 * PlayerModal.jsx — RotoTube lightbox player, YouTube "watch"-style.
 *
 * Renders whatever a media item needs: a <video> (Vimeo / Frame.io signed proxy,
 * streamed from the source CDN via the backend redirect), a Cloudflare Stream
 * <iframe> (HDPhotoHub delivery videos), or a full-res <img> (HDPhotoHub stills).
 * Below the player: a title and a YouTube-style action pill row (Favorite / Save /
 * Share / Download). `item` is null when closed; `actions` is optional so public
 * consumers can reuse this without any of the internal tools.
 */

import { useState, useEffect } from 'react';
import { Dialog, Box, Typography, IconButton, Button } from '@mui/material';
import {
  Close as CloseIcon, Favorite as HeartFilledIcon, FavoriteBorder as HeartIcon,
  PlaylistAdd as SaveIcon, Share as ShareIcon, Download as DownloadIcon,
  VideocamOff as FailedIcon,
} from '@mui/icons-material';
import { YT } from './ytTheme';
import { useMobile } from '../../hooks/useMobile';

function Pill({ icon, label, onClick, href, active }) {
  return (
    <Button
      onClick={onClick}
      {...(href ? { component: 'a', href, download: true } : {})}
      startIcon={icon}
      sx={{
        textTransform: 'none', borderRadius: '999px', px: 1.75, py: 0.5, minWidth: 0,
        bgcolor: active ? 'rgba(255,0,0,0.15)' : YT.surfaceHover, color: active ? YT.accent : YT.text,
        fontSize: '0.85rem', fontWeight: 500, '&:hover': { bgcolor: active ? 'rgba(255,0,0,0.25)' : YT.chipHover },
      }}
    >
      {label}
    </Button>
  );
}

export default function PlayerModal({ item, onClose, actions }) {
  const { isMobile } = useMobile();
  const open = Boolean(item);
  // A dead source URL (rotated/expired upstream) otherwise renders a silent black box.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [item?.src]);
  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: '#0f0f0f', backgroundImage: 'none' } }}>
      {item && (
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ width: '100%', aspectRatio: item.kind === 'image' ? 'auto' : '16 / 9', bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {failed ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 8, color: YT.textSecondary }}>
                <FailedIcon sx={{ fontSize: 40, opacity: 0.6 }} />
                <Typography variant="body2">Couldn&apos;t load this media — the source may be temporarily unavailable.</Typography>
              </Box>
            ) : (
              <>
                {item.kind === 'video-url' && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption -- source CDN clips have no caption tracks
                  <video key={item.src} src={item.src} controls autoPlay playsInline onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}
                {item.kind === 'stream-iframe' && (
                  <iframe
                    key={item.src} src={item.src} title={item.title || 'video'}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen style={{ border: 0, width: '100%', height: '100%' }}
                  />
                )}
                {item.kind === 'image' && (
                  <img src={item.src} alt={item.title || ''} onError={() => setFailed(true)} style={{ maxWidth: '100%', maxHeight: '82vh', objectFit: 'contain' }} />
                )}
              </>
            )}
          </Box>

          {/* Title + action row */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {item.title && (
              <Typography sx={{ color: YT.text, fontSize: '1.15rem', fontWeight: 600, lineHeight: 1.35 }}>
                {item.title}
              </Typography>
            )}
            {actions && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {actions.onFavorite && (
                  <Pill
                    icon={actions.favorited ? <HeartFilledIcon fontSize="small" /> : <HeartIcon fontSize="small" />}
                    label={actions.favorited ? 'Favorited' : 'Favorite'} active={actions.favorited}
                    onClick={actions.onFavorite}
                  />
                )}
                {actions.onAddToCollection && <Pill icon={<SaveIcon fontSize="small" />} label="Save" onClick={actions.onAddToCollection} />}
                {actions.onShare && <Pill icon={<ShareIcon fontSize="small" />} label="Share" onClick={actions.onShare} />}
                {actions.downloadHref && <Pill icon={<DownloadIcon fontSize="small" />} label="Download" href={actions.downloadHref} />}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
